import { Component, markRaw, nextTick, ref } from "vue";
import MkWaitingDialog from "@/components/MkWaitingDialog.vue";
import { intoResultFuture, Result } from "@/types/result";
import { v7 } from "uuid";
import { messageAlert } from "@/composables/message-box";
import { ComponentPropsMaybeRef, Emits } from "@/component-types";
import {
    placePopover,
    removePopover,
    spawnPopover,
} from "@/components/layout/popover-layer";

export async function asyncDialog<T>(
    promise: Promise<T>,
): Promise<Result<T, any>> {
    const wrapperPromise = new Promise<Result<T, any>>((resolve, reject) => {
        promiseDialog(
            promise,
            (result) => {
                resolve(result);
            },
            (err) => {
                messageAlert({
                    type: "error",
                    text: err.message + "\n" + err?.["id"],
                });
                reject(err);
            },
        );
    });

    return intoResultFuture(wrapperPromise);
}

export function promiseDialog<T extends Promise<any>>(
    promise: T,
    onSuccess?: ((res: any) => void) | null,
    onFailure?: ((err: Error) => void) | null,
    text?: string,
): T {
    const showing = ref(true);
    const success = ref(false);

    promise
        .then((res) => {
            if (onSuccess) {
                showing.value = false;
                onSuccess(res);
            } else {
                success.value = true;
                window.setTimeout(() => {
                    showing.value = false;
                }, 1000);
            }
        })
        .catch((err) => {
            showing.value = false;
            if (onFailure) {
                onFailure(err);
            } else {
                messageAlert({
                    type: "error",
                    text: err,
                });
            }
        });

    spawnPopover(
        MkWaitingDialog,
        {
            success: success,
            showing: showing,
            text: text,
        },
        "closed",
    );

    return promise;
}

export async function popup<C extends Component>(
    component: C,
    props: ComponentPropsMaybeRef<NoInfer<C>>,
    events: Partial<Emits<NoInfer<C>>> = {},
    disposeEvent?: keyof Emits<NoInfer<C>>,
) {
    const id = v7();

    const dispose = async () => {
        await nextTick();
        removePopover(id);
    };

    placePopover({
        kind: "legacy-dialog",
        id,
        component: markRaw(component),
        props,
        events: disposeEvent
            ? {
                  ...events,
                  [disposeEvent]: dispose,
              }
            : events,
    });

    return {
        dispose,
    };
}
